import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMemo } from "react";
import { useForm, type FieldPath } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { PageContainer } from "@/components/ui/page-container";
import { MobilePageTitle } from "@/components/ui/mobile-page-title";
import { Wizard } from "@/components/ui/wizard";
import type { WizardStep } from "@/components/ui/wizard/types";
import { ConfirmDialog } from "@/components/ui/dialog/confirm-dialog";
import { toast } from "@/components/ui/toast";
import { PROTECTED_ROUTES } from "@/utils/navigation";
import { useUnsavedChanges } from "@/hooks/use-unsaved-changes";
import { getApiErrorMessages } from "@/utils/api-error";
import { stripSpaces } from "@/utils/input-masks";
import {
  AthleteCreationSchema,
  type AthleteCreationFormData,
} from "@/schemas/athlete";
import {
  GeneralInfoStep,
  HealthEmergencyStep,
  FiscalStep,
  ReviewStep,
} from "@/components/create-athlete/steps";
import { ID_DOCUMENT_TYPE } from "@/constants/athlete";
import { useCreateAthlete } from "@/queries/use-athletes";
import { useGymStore } from "@/stores/gym-store";
import type {
  AthleteAddressRequestData,
  RegisterAthleteRequestData,
} from "@/api/athletes/types";

export const Route = createFileRoute("/(protected)/athletes/new/")({
  component: RouteComponent,
});

const GENERAL_INFO_FIELDS: FieldPath<AthleteCreationFormData>[] = [
  "firstName",
  "lastName",
  "email",
  "birthdate",
  "phoneNumber",
  "landlineNumber",
  "gender",
  "maritalStatus",
  "nationality",
  "idDocumentType",
  "idDocumentNumber",
  "idDocumentExpirationDate",
  "addressLine1",
  "city",
  "postalCode",
];

const HEALTH_EMERGENCY_FIELDS: FieldPath<AthleteCreationFormData>[] = [
  "emergencyContactName",
  "emergencyContactPhoneNumber",
];

const FISCAL_FIELDS: FieldPath<AthleteCreationFormData>[] = [
  "fiscalFullName",
  "taxId",
  "iban",
  "billingAddressLine1",
  "billingCity",
  "billingPostalCode",
];

const DEFAULT_VALUES: AthleteCreationFormData = {
  avatar: "",
  firstName: "",
  lastName: "",
  email: "",
  birthdate: "",
  phoneNumber: "",
  landlineNumber: "",
  gender: "",
  maritalStatus: "",
  nationality: "",
  idDocumentType: ID_DOCUMENT_TYPE.CitizenCard,
  idDocumentNumber: "",
  idDocumentExpirationDate: "",
  addressLine1: "",
  addressLine2: "",
  city: "",
  postalCode: "",
  emergencyContactName: "",
  emergencyContactPhoneNumber: "",
  emergencyContactRelationship: "",
  medicalConditions: "",
  allergiesIntolerances: "",
  currentMedication: "",
  fiscalFullName: "",
  taxId: "",
  iban: "",
  billingAddressSameAsMain: true,
  billingAddressLine1: "",
  billingAddressLine2: "",
  billingCity: "",
  billingPostalCode: "",
};

function RouteComponent() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { selectedGym } = useGymStore();
  const { mutate: createAthlete, isPending } = useCreateAthlete();

  const form = useForm<AthleteCreationFormData>({
    resolver: zodResolver(AthleteCreationSchema()),
    mode: "onSubmit",
    defaultValues: DEFAULT_VALUES,
  });

  const steps: WizardStep<AthleteCreationFormData>[] = useMemo(
    () => [
      {
        id: "general-info",
        label: t("pages:athletes.wizard.steps.generalInfo"),
        component: GeneralInfoStep,
        fields: GENERAL_INFO_FIELDS,
      },
      {
        id: "health-emergency",
        label: t("pages:athletes.wizard.steps.healthEmergency"),
        component: HealthEmergencyStep,
        fields: HEALTH_EMERGENCY_FIELDS,
      },
      {
        id: "fiscal",
        label: t("pages:athletes.wizard.steps.fiscal"),
        component: FiscalStep,
        fields: FISCAL_FIELDS,
      },
      {
        id: "confirmation",
        label: t("pages:athletes.wizard.steps.confirmation"),
        component: ReviewStep,
      },
    ],
    [t],
  );

  const navigateBack = () => {
    navigate({ to: PROTECTED_ROUTES.ATHLETES });
  };

  const { handleCancelEdit, unsavedChangesDialogProps } = useUnsavedChanges({
    isEditing: true,
    isDirty: form.formState.isDirty,
    onLeave: navigateBack,
  });

  const handleComplete = (data: AthleteCreationFormData) => {
    if (!selectedGym?.id) {
      toast.error(t("pages:athletes.wizard.toaster.error.title"), {
        description: t("pages:athletes.wizard.toaster.error.description"),
      });
      return;
    }

    const mainAddress: AthleteAddressRequestData = {
      addressLine1: data.addressLine1,
      addressLine2: data.addressLine2 || undefined,
      city: data.city,
      country: "PT",
      postalCode: data.postalCode,
    };

    // The billing address is always stored, so mirroring the main one is a copy
    // rather than an omission.
    const billingAddress: AthleteAddressRequestData =
      data.billingAddressSameAsMain
        ? mainAddress
        : {
            addressLine1: data.billingAddressLine1 ?? "",
            addressLine2: data.billingAddressLine2 || undefined,
            city: data.billingCity ?? "",
            country: "PT",
            postalCode: data.billingPostalCode ?? "",
          };

    // The IBAN is validated ignoring spaces and casing, so normalise it here
    // to send the API the same value the validation accepted.
    const iban = stripSpaces(data.iban)?.toUpperCase();

    const payload: RegisterAthleteRequestData = {
      athlete: {
        gymId: selectedGym.id.toString(),
        userIdentity: {
          email: data.email,
        },
        userProfile: {
          firstName: data.firstName,
          lastName: data.lastName,
          birthdate: data.birthdate,
          phoneNumber: stripSpaces(data.phoneNumber) ?? "",
          landlineNumber: stripSpaces(data.landlineNumber),
          gender: data.gender,
          maritalStatus: data.maritalStatus,
          nationality: data.nationality,
          idDocumentType: data.idDocumentType,
          idDocumentNumber: data.idDocumentNumber,
          idDocumentExpirationDate: data.idDocumentExpirationDate,
          avatar: data.avatar,
        },
        addresses: {
          "0": { ...mainAddress, main: true },
        },
        emergencyContact: {
          name: data.emergencyContactName,
          phone: stripSpaces(data.emergencyContactPhoneNumber) ?? "",
          relationship: data.emergencyContactRelationship || undefined,
        },
        healthConsideration: {
          medicalConditions: data.medicalConditions || undefined,
          allergiesIntolerances: data.allergiesIntolerances || undefined,
          currentMedication: data.currentMedication || undefined,
        },
        billingDetail: {
          taxId: data.taxId,
          fiscalFullName: data.fiscalFullName,
          iban: iban || undefined,
          billingAddress,
        },
      },
    };

    createAthlete(payload, {
      onSuccess: () => {
        toast.success(t("pages:athletes.wizard.toaster.success.title"), {
          description: t("pages:athletes.wizard.toaster.success.description"),
        });
        navigateBack();
      },
      onError: (error) => {
        toast.error(t("pages:athletes.wizard.toaster.error.title"), {
          description: getApiErrorMessages(error),
        });
      },
    });
  };

  const handleCancel = () => {
    handleCancelEdit(navigateBack);
  };

  return (
    <div className="flex h-full flex-col px-6 py-5">
      <MobilePageTitle>{t("pages:athletes.wizard.title")}</MobilePageTitle>
      <PageContainer
        className="flex min-h-0 min-w-[309px] flex-1 flex-col p-0 md:m-0 lg:mb-0"
        internalScroll
      >
        <Wizard
          form={form}
          steps={steps}
          onComplete={handleComplete}
          isSubmitting={isPending}
          onCancel={handleCancel}
        />
      </PageContainer>
      <ConfirmDialog {...unsavedChangesDialogProps} />
    </div>
  );
}
